/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpEventType} from '@angular/common/http';
import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTabGroup} from '@angular/material/tabs';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {BUSINESS_PLAN, DISMISS_MSG, TVP, VEHICLE_COLUMN_HEADERS} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {StateService} from '../../../core/state/state.service';
import {LookupTableLoadError} from '../../../models/lookup-table-load-error.model';
import {MCMData} from '../../../models/mcm-data.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {User} from '../../../models/user.model';
import {VehiclePortfolioLoad} from '../../../models/vehicle-portfolio-load.model';
import {VehicleSnapshotViolation} from '../../../models/vehicle-snapshot-violation.model';
import {VehicleSnapshot} from '../../../models/vehicle-snapshot.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {LookupTableService} from '../../../services/lookup-table.service';
import {VehicleSnapshotService} from '../../../services/vehicle-snapshot.service';

enum VIOLATION_TABS {
  BLANK_CELLS = 'Blank Cell Violations',
  INVALID_MKT_DIV = 'Invalid Market Divisions',
  INVALID_PLNG_MKT = 'Invalid Planning Markets',
  MULT_ARCH = 'Multiple Arch Violations',
  MULT_ENG_CTR = 'Multiple Eng Ctr Violations',
  DATE_VIOLATION = 'Date Violations'
}

export const EXPORT_DATA_ERROR = 'Error with exporting data.';

@Component({
    selector: 'app-vehicle-snapshot',
    templateUrl: './vehicle-snapshot.component.html',
    styleUrls: ['./vehicle-snapshot.component.scss'],
    standalone: false
})
export class VehicleSnapshotComponent implements OnInit {

  @ViewChild('violationTabGroup') violationTabGroup: MatTabGroup;

  user: User;

  showSpinner: boolean;
  showTable: boolean;
  hasViolations: boolean;
  isKeepRecId: boolean;

  pageSize: number;
  violationTabIndex: number;
  recIdStart: number;

  // values for Portfolio type radio options
  selectedPortfolioType: string;
  businessPlan = BUSINESS_PLAN;
  tvp = TVP;

  tableDataExportRequest: WorksheetExportRequest;
  processedSnapshotExportRequest: WorksheetExportRequest;
  file: File;
  sheets: SelectableOptions = new SelectableOptions();
  selectedVehSnapshotSheet = [];
  selectedMcmDataSheet = [];

  lookupTableList: string[] = [];
  selectedLookupTables: string[] = [];
  lookupTableLoadErrors: LookupTableLoadError[] = [];
  displayedData: VehicleSnapshot[];
  vehicleSnapshotData: Map<string, VehicleSnapshot[]>;
  processedSnapshot: Map<string, VehiclePortfolioLoad[]>;
  lookupTableData: Map<string, any[]>;
  violations: VehicleSnapshotViolation;
  mcmData: MCMData[] = [];

  vehSnapshotColDef: ColDef[] = [
    {
      field: 'compositeKey.iteration',
      headerName: 'Iteration Number',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.sevenKeyFields.brand',
      headerName: 'Brand',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'region', headerName: 'Region of Sale', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'subRegion', headerName: 'Sub-Region', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'compositeKey.sevenKeyFields.plngMkt',
      headerName: 'Market Operation',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'cntryRpo', headerName: 'Country RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'compositeKey.sevenKeyFields.mktDiv',
      headerName: 'Marketing Division',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.sevenKeyFields.lbrand',
      headerName: 'Local Brand',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.sevenKeyFields.bodyStyle',
      headerName: 'Body Style',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.sevenKeyFields.styleCode',
      headerName: 'Bodystyle Code',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.drvPos',
      headerName: 'Driver Position',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'arch', headerName: 'Architecture', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'compositeKey.sevenKeyFields.prgmCode',
      headerName: 'Program Code',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'engCtr', headerName: 'VEC', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'compositeKey.gfsVehicleDates.firstMy',
      headerName: 'First MY',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.gfsVehicleDates.lastMy',
      headerName: 'Last MY',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.gfsVehicleDates.sorp',
      headerName: 'Vehicle SORP',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.gfsVehicleDates.eop',
      headerName: 'Vehicle EOP',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.gfsVehicleDates.sos',
      headerName: 'Vehicle SOS',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'compositeKey.gfsVehicleDates.eos',
      headerName: 'Vehicle EOS',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'vehType', headerName: 'Vehicle Type', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'gseg', headerName: 'Global Segment', filter: AutocompleteFilterComponent, lockPosition: true}
  ];

  vehSnapshotColumns: ColumnHeader[] = this.vehSnapshotColDef.map(col => {
    return {name: col.field, label: col.headerName}
  });

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  vehiclePortfolioColumns: ColumnHeader[] = VEHICLE_COLUMN_HEADERS;

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              private readonly vehicleSnapshotService: VehicleSnapshotService,
              private readonly lookupTableService: LookupTableService,
              private readonly userService: StateService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.pageSize = 100;
    this.selectedPortfolioType = this.businessPlan.toUpperCase();
    this.isKeepRecId = true;
    this.recIdStart = 1;
    this.getLookupTableList();
  }

  /**
   * fetches data from backend,
   * if list is empty, hides table and displays message telling the user the table is empty
   * @return void
   */
  getVehicleSnapshot(): void {
    this.showSpinner = true;
    this.showTable = false;
    this.vehicleSnapshotService.getVehicleSnapshot().subscribe(
      (data: VehicleSnapshot[]) => {
        this.displayedData = data;
      },
      err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      () => {
        this.showSpinner = false;
        this.showTable = true;
        this.setTableDataExportRequest();
      }
    );
  }

  /**
   * Transform the Vehicle Snapshot file to VehicleSnapshot data that can be worked with
   */
  transformVehicleSnapshot(): void {
    this.showSpinner = true;
    this.vehicleSnapshotService.transform(this.file, this.selectedVehSnapshotSheet).subscribe(e => {
      if (e.type === HttpEventType.Response) {
        this.vehicleSnapshotData = e.body;
      }
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.validate();
    });
  }

  /**
   * Valdiate the vehicle snapshot
   */
  validate(): void {
    this.showSpinner = true;
    this.vehicleSnapshotService.validate(this.vehicleSnapshotData).subscribe(data => {
      this.violations = data[this.selectedVehSnapshotSheet[0]];
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      const firstNonEmptyValidationList: string = this.getFirstNonEmptyValidationList();
      this.hasViolations = this.violations !== undefined && firstNonEmptyValidationList !== undefined;
      if (this.hasViolations) {
        this.snackBar.displayError('There were violations found in the vehicle snapshot', DISMISS_MSG);
        this.displayedData = this.violations[firstNonEmptyValidationList];
        this.showTable = true;
        this.setTableDataExportRequest();
        this.showSpinner = false;
      } else {
        // If there are no violations, load the data
        this.loadVehicleSnapshot();
      }
    });
  }

  /**
   * Load the vehicle snapshot data into the system
   */
  loadVehicleSnapshot(): void {
    this.showSpinner = true;
    this.vehicleSnapshotService.load(this.vehicleSnapshotData).subscribe(data => {
        this.vehicleSnapshotData = data;
      }, err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.snackBar.displaySuccess('Vehicle Snapshot was loaded successfully.', DISMISS_MSG);
        this.displayedData = this.vehicleSnapshotData[this.selectedVehSnapshotSheet[0]];
        this.showTable = true;
        this.setTableDataExportRequest();
        this.showSpinner = false;
      }
    );
  }

  /**
   * Used to transform the Excel sheet that contains the MCM data
   */
  transformMCMData(): void {
    this.showSpinner = true;
    this.vehicleSnapshotService.transformMCMData(this.file, this.selectedMcmDataSheet).subscribe(e => {
      if (e.type === HttpEventType.Response) {
        this.mcmData = e.body[this.selectedMcmDataSheet[0]];
      }
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.showSpinner = false;
      this.loadMCMData();
    });
  }

  /**
   * Load MCM Data into the system
   */
  loadMCMData(): void {
    this.showSpinner = true;
    this.vehicleSnapshotService.loadMCMData(this.mcmData).subscribe({
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('MCM Data was loaded successfully. Now loading vehicle snapshot...', DISMISS_MSG);
        // Once the MCM data loading is finished, load the vehicle snapshot
        this.transformVehicleSnapshot();
      }
    });
  }

  /**
   * Process the Vehicle Snapshot in the system and export the processed snapshot as a Vehicle Portfolio
   */
  processSnapshot(): void {
    this.showSpinner = true;
    this.vehicleSnapshotService.processSnapshot('GZCFT8', this.recIdStart, this.selectedPortfolioType, this.isKeepRecId).subscribe(
      data => {
        this.processedSnapshot = data;
      }, err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.exportProcessedSnapshot();
      });
  }

  /**
   * Export the processed snapshot data to Excel
   */
  exportProcessedSnapshot(): void {
    const sheetList: [string, ColumnHeader[]][] = [];
    Object.keys(this.processedSnapshot).forEach(key => {
      sheetList.push([key, this.vehiclePortfolioColumns]);
    });
    const numTypeFields = new Map<string, string[]>();
    Object.keys(this.processedSnapshot).forEach(key => {
      numTypeFields[key] = ['firstMy', 'lastMy'];
    });
    const currDate = new Date().toISOString();
    this.showSpinner = true;
    this.processedSnapshotExportRequest = this.excelService.createExportRequestBody('VehPortForGFSLoad_' + currDate,
      this.processedSnapshot,
      sheetList,
      numTypeFields);
    this.excelService.transformToExcel(this.processedSnapshotExportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, this.processedSnapshotExportRequest.fileName);
      },
      () => {
        this.snackBar.displayError(EXPORT_DATA_ERROR, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Sets the export data request for exporting the table data
   */
  setTableDataExportRequest(): void {
    // If the table data is displaying violations, then export those sheets, else just export the table data
    setTimeout(() => {
      if (this.hasViolations) {
        this.setViolationExportRequest();
      } else {
        const currDate = new Date().toISOString();
        const excelData = new Map<string, any>();
        excelData['VP_GM3PD_Vehicle_Snapshot'] = this.displayedData;

        // Send the request
        this.tableDataExportRequest = this.excelService.createExportRequestBody('CurrentGM3PDVehSnap_' + currDate,
          excelData,
          [['VP_GM3PD_Vehicle_Snapshot', this.vehSnapshotColumns]]);
      }
    });
  }

  /**
   * Helper function to create the Export request for the violation sheets
   */
  setViolationExportRequest(): void {
    const currDate = new Date().toISOString();
    const violationTabs: [string, ColumnHeader[]][] = this.violationTabGroup._allTabs.toArray().map(tab => {
      return [tab.textLabel, this.vehSnapshotColumns];
    });
    const data = new Map<string, any>();
    data[VIOLATION_TABS.MULT_ARCH] = this.violations.multArchPerSevenKeyList;
    data[VIOLATION_TABS.MULT_ENG_CTR] = this.violations.multEngCtrPerSevenKeyList;
    data[VIOLATION_TABS.DATE_VIOLATION] = this.violations.dateViolations;
    data[VIOLATION_TABS.BLANK_CELLS] = this.violations.blankCellViolations;
    data[VIOLATION_TABS.INVALID_PLNG_MKT] = this.violations.invalidPlngMktList;
    data[VIOLATION_TABS.INVALID_MKT_DIV] = this.violations.invalidMktDivList;
    // Send the request
    this.tableDataExportRequest = this.excelService.createExportRequestBody('GM3PDVehSnapViolations_' + currDate,
      data,
      violationTabs);
  }

  /**
   * Event handler for when an upload event is emitted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    this.dialog.open(SheetSelectorComponent, {
      data: {
        sheets: this.sheets.options,
        isMultiSelect: false
      },
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        // Clear out the selected options before setting them in case the user already uploaded a file and
        // selected an option previously
        this.sheets.selectedOptions = [];
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0) {
          this.selectedVehSnapshotSheet = this.sheets.selectedOptions;
          this.openMcmDataConfirmation();
        }
      }
    );
  }

  /**
   * Open confirmation dialog to ask whether or not the User wants to load new MCM data
   */
  openMcmDataConfirmation(): void {
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'Would you like to load MCM data?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    }).afterClosed().subscribe({
      next: confirmation => {
        if (confirmation) {
          this.openMcmDataSheetSelector();
        } else {
          // If user chooses to not load new MCM data, then just load the vehicle snapshot
          this.transformVehicleSnapshot();
        }
      }
    });
  }

  /**
   * Opens up the sheet selector dialog for choosing the Excel sheet that contains the MCM data
   */
  openMcmDataSheetSelector(): void {
    let isCancel = false;
    this.dialog.open(SheetSelectorComponent, {
      data: {
        sheets: this.sheets.options,
        isMultiSelect: false
      },
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
        } else {
          isCancel = true;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0 && !isCancel) {
          this.selectedMcmDataSheet = this.sheets.selectedOptions;
          this.transformMCMData();
        } else if (isCancel) {
          this.transformVehicleSnapshot();
        }
      }
    );
  }

  /**
   * Gets the name of the first non-empty validation list
   * @return string - name of the first non-empty validation list, undefined if not found
   */
  getFirstNonEmptyValidationList(): string {
    return Object.keys(this.violations).find(key => this.violations[key].length > 0);
  }

  /**
   * Update the datasource of the Violation table data based off the tab change
   * @param $event The event that is emitted when a tab was changed
   */
  violationTabChange($event): void {
    if ($event !== undefined && $event.index !== -1) {
      switch ($event.tab.textLabel) {
        case VIOLATION_TABS.BLANK_CELLS:
          this.displayedData = this.violations.blankCellViolations;
          break;
        case VIOLATION_TABS.INVALID_MKT_DIV:
          this.displayedData = this.violations.invalidMktDivList;
          break;
        case VIOLATION_TABS.INVALID_PLNG_MKT:
          this.displayedData = this.violations.invalidPlngMktList;
          break;
        case VIOLATION_TABS.MULT_ARCH:
          this.displayedData = this.violations.multArchPerSevenKeyList;
          break;
        case VIOLATION_TABS.MULT_ENG_CTR:
          this.displayedData = this.violations.multEngCtrPerSevenKeyList;
          break;
        case VIOLATION_TABS.DATE_VIOLATION:
          this.displayedData = this.violations.dateViolations;
          break;
        default:
          break;
      }
    }
  }

  /********************************
   * FUNCTIONS FOR LOOKUP TABLE ***
   ********************************/

  /**
   * Get list of Lookup tables in GFS
   */
  getLookupTableList(): void {
    this.showSpinner = true;
    this.lookupTableService.getLookupTableList().subscribe(data => {
      this.lookupTableList = data;
    }, error => {
      this.showSpinner = false;
      this.snackBar.displayError(error, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
    });
  }

  /**
   * Get data for the selected lookup tables and download them
   */
  downloadLookupTable(): void {
    this.showSpinner = true;
    this.lookupTableService.getLookupTableData(this.selectedLookupTables).subscribe(data => {
      this.lookupTableData = data;
    }, error => {
      this.snackBar.displayError(error, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.exportLookupTables();
    });
  }

  /**
   * Function used to export the Lookup tables to Excel
   */
  exportLookupTables(): void {
    this.showSpinner = true;
    const currDate = new Date().toISOString();
    const exportRequest = new WorksheetExportRequest();
    exportRequest.fileName = 'LookupTables_' + currDate;
    exportRequest.data = this.lookupTableData;

    const headers: Map<string, string[]> = new Map<string, string[]>();
    Object.keys(this.lookupTableData).forEach(key => {
      if (this.lookupTableData[key].length > 0) {
        headers[key] = Object.keys(this.lookupTableData[key][0]);
      }
    });
    exportRequest.headers = headers;
    exportRequest.fieldNames = headers;
    this.excelService.transformToExcel(exportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, exportRequest.fileName);
      },
      () => {
        this.snackBar.displayError(EXPORT_DATA_ERROR, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Event handler for when an upload event is emitted for loading Lookup table Excel file.
   * @param event The event that is emitted by the Upload button component
   */
  uploadLookupTable(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onLookupTableUpload();
  }

  /**
   * Method run after uploading an Excel file for Lookup tables.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onLookupTableUpload(): void {
    let isCancel = false;
    this.dialog.open(SheetSelectorComponent, {
      data: {
        sheets: this.sheets.options,
        isMultiSelect: true
      },
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
        } else {
          isCancel = true;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0 && !isCancel) {
          this.showSpinner = true;
          this.lookupTableService.loadLookupTables(this.file, this.sheets.selectedOptions, this.user.gmid).subscribe((e) => {
              if (e.type === HttpEventType.Response) {
                this.lookupTableLoadErrors = e.body;
              }
            },
            error => {
              this.snackBar.displayError(error, DISMISS_MSG);
              this.showSpinner = false;
            }, () => {
              this.showSpinner = false;
              // If there are any errors with the Lookup table load, export the errors to Excel
              if (this.lookupTableLoadErrors.length !== 0) {
                const tablesWithErrors = [...new Set(this.lookupTableLoadErrors.map(row => row.tableName))];
                this.dialog.open(FabConfirmationDialogComponent, {
                  width: '400px',
                  data: {
                    title: 'Lookup Table Load Error',
                    message: 'The following tables were not loaded (contains errors): ' + tablesWithErrors.join(', '),
                    yesLabel: 'OK',
                    titleIcon: 'warning',
                    actionColor: 'primary'
                  },
                  disableClose: true
                });
                this.exportLookupTableLoadErrors();
              } else {
                this.snackBar.displaySuccess('Successfully loaded lookup tables', DISMISS_MSG);
              }
            });
        }
      }
    );
  }

  /**
   * Function used to export Lookup table load errors to Excel
   */
  exportLookupTableLoadErrors(): void {
    this.showSpinner = true;
    const currDate = new Date().toISOString();
    const exportRequest = new WorksheetExportRequest();
    exportRequest.fileName = 'LookupTableErrors_' + currDate;
    const data: Map<string, any[]> = new Map<string, any[]>();
    data['Errors'] = this.lookupTableLoadErrors;
    exportRequest.data = data;

    const headers: Map<string, string[]> = new Map<string, string[]>();
    headers['Errors'] = Object.keys(this.lookupTableLoadErrors[0]);
    exportRequest.headers = headers;
    exportRequest.fieldNames = headers;
    this.excelService.transformToExcel(exportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, exportRequest.fileName);
      },
      () => {
        this.snackBar.displayError(EXPORT_DATA_ERROR, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }
}
